import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { InputWidth } from 'nhsuk-react-components/dist/esm/util/types/NHSUKTypes';
import { TextBoxProps } from '@form-controls';
import { TextInput } from 'nhsuk-react-components';

interface TextBoxState {
    error: string | undefined;
    isReadOnly: boolean;
    isValid: boolean;
}

const TextBox: React.FC<TextBoxProps> = forwardRef((props, ref) => {
    const [state, setState] = useState<TextBoxState>({
        error: undefined,
        isReadOnly: props.question.isReadOnly,
        isValid: false
    });

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            isReadOnly: props.question.isReadOnly
        }));
    }, [props.question.isReadOnly]);

    useImperativeHandle(ref, () => ({
        validate,
        isValid: state.isValid
    }));

    const toggleConditionalRevealQuestions = () => {
        const parentQuestion = props.questions.find(
            question => question.id === props.question.parent_id
        );

        if (!parentQuestion) {
            return;
        }

        const parentValue = parentQuestion.value;

        const linkedQuestion = parentQuestion.linked_questions?.find(
            (question: any) => question.id === props.question.id
        );

        if (!linkedQuestion || !parentValue) {
            return;
        }

        let isRevealed = false;
        for (const answer of parentValue) {
            const option = parentQuestion.options.find((option: any) => answer === option.key);

            if (!option.hide_linked_question) {
                isRevealed = true;
                break;
            }
        }

        props.onConditionalReveal({
            isRevealed,
            questionIds: [props.question.id]
        });
    };

    useEffect(() => {
        toggleConditionalRevealQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.questions, props.question.parent_id]);

    const validate = () => {
        const value = getValue();
        const questionName = props.question.name;
        const max = props.question.max_length;
        const min = props.question.min_length;

        if (props.question.mandatory) {
            if (value.length === 0) {
                setState(prev => ({
                    ...prev,
                    error: `Enter the ${questionName}`,
                    isValid: false
                }));
            } else if (min || max) {
                validateMinMax(min, max, value, questionName);
            } else {
                setState(prev => ({
                    ...prev,
                    error: undefined,
                    isValid: true
                }));
            }
        } else if (min || max) {
            validateMinMax(min, max, value, questionName);
        } else {
            setState(prev => ({
                ...prev,
                error: undefined,
                isValid: true
            }));
        }
    };

    const validateMinMax = (
        min: number | undefined,
        max: number | undefined,
        value: string,
        questionName: string
    ) => {
        let error = '';
        let isValid = true;

        if (max && min && (value.length > max || value.length < min) && value.length > 0) {
            error = `${questionName} must be between ${min} and ${max} characters`;
            isValid = false;
        } else if (min && value.length < min && value.length > 0) {
            error = `${questionName} must be minimum ${min} characters in length`;
            isValid = false;
        } else if (max && value.length > max && value.length > 0) {
            error = `${questionName} exceeds the max length of ${max} characters`;
            isValid = false;
        }

        setState(prev => ({
            ...prev,
            error,
            isValid
        }));
    };

    const getValue = (): string => {
        return props.question.value ?? '';
    };

    const getWidth = (): InputWidth => {
        const validWidthValues: InputWidth[] = [2, 3, 4, 5, 10, 20, 30];

        if (props.question.width && validWidthValues.includes(props.question.width)) {
            return props.question.width;
        }

        return 10;
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        props.onChange({
            questionId: props.question.id,
            value: event.target.value
        });

        props.question.value = event.target.value;

        validate();
    };

    const value = getValue();

    return !props.question.hidden ? (
        <div>
            <TextInput
                id={props.id}
                label={props.question.name}
                hint={props.question.hint_text}
                error={state.error}
                width={getWidth()}
                value={value}
                onChange={handleChange}
                onBlur={handleChange}
                disabled={state.isReadOnly}
                required={props.question.mandatory}
            />
        </div>
    ) : null;
});

export default TextBox;
